import React, { FC } from 'react'
import { createUseStyles } from 'react-jss'
import { useAtom } from 'jotai'
import { colors } from '../../../theme/palette'
import { Close } from '../../../assets/icons'
import {
    FilterPosition,
    FilterTRFor,
    FilterTypeOfRepresentative,
    FilterWorkGroup,
    FilterWorkplace,
    FilterUpdateStatus,
    initialFilter,
} from '../../../models/Filter'
import {
    updateGroups,
    updateTypeOfRepresentatives,
    updatePositions,
    updateWorkplaces,
    updateTRFor,
    updateUpdateStatus,
} from '../../../utils/filterhelper'
import { filteredAtom, filterSelected } from '../../../store/filter/filterAtom'
import { Typography } from '../../ui/Typography/Typography'
import { baseButton } from '../../../theme/reuseables'

interface IPillProps {
    label: string
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void
}

const useStyles = createUseStyles({
    pills: {
        display: 'flex',
        height: 'fit-content',
        gap: '10px',
        flexWrap: 'wrap',
        padding: '.4rem 1rem 0 0',
    },
    numberOfMembers: {
        display: 'flex',
        flexShrink: '0',
        padding: '.8rem 1rem 0 0',
    },
    container: {
        display: 'flex',
    },
    button: {
        ...baseButton,
        ...{
            cursor: 'pointer',
            margin: 0,
            padding: 0,
        },
    },
})

const pillStyles = createUseStyles({
    pill: {
        display: 'flex',
        flexShrink: 0,
        border: `1px solid ${colors.gray600}`,
        background: '#fff',
        color: '#000',
        padding: '0.375rem 0.625rem',
        borderRadius: '8px',
        transition: '.3s',
        '&:hover': {
            cursor: 'pointer',
            backgroundColor: colors.green500,
            color: colors.white,
        },
    },
    closeIcon: {
        backgroundColor: colors.green500,
        borderRadius: '50%',
        aspectRatio: '1/1',
        padding: '.2rem',
        width: 'fit-content',
        display: 'flex',
        alignItems: 'center',
        marginLeft: '5px',
        '& path': {
            strokeWidth: '6px',
        },
    },
})
export const Pill: FC<IPillProps> = ({ label, onClick }) => {
    const classes = pillStyles()

    return (
        <button className={classes.pill} type="button" onClick={onClick}>
            <span>{label}</span>
            <span className={classes.closeIcon}>
                <Close width="14px" color={colors.white} />
            </span>
        </button>
    )
}

export const FilterSummary: FC = () => {
    const [members] = useAtom(filteredAtom)
    const classes = useStyles()
    const [selected, setSelected] = useAtom(filterSelected)

    const onUpdateWorkplaces = (workPlace: FilterWorkplace): void => {
        const items = updateWorkplaces(workPlace, selected)
        setSelected(items)
    }

    const onUpdateTrFor = (trFor: FilterTRFor): void => {
        const items = updateTRFor(trFor, selected)
        setSelected(items)
    }

    const onUpdateGroups = (workGroup: FilterWorkGroup): void => {
        const items = updateGroups(workGroup, selected)
        setSelected(items)
    }

    const onUpdatePositions = (position: FilterPosition): void => {
        const items = updatePositions(position, selected)
        setSelected(items)
    }

    const onUpdateTypeOfRepresentatives = (
        pot: FilterTypeOfRepresentative
    ): void => {
        const items = updateTypeOfRepresentatives(pot, selected)
        setSelected(items)
    }

    const onUpdateUpdateStatus = (status: FilterUpdateStatus): void => {
        const items = updateUpdateStatus(status, selected)
        setSelected(items)
    }

    const ifAny = (): boolean => {
        const { positions, workplaces, typeOfRepresentative, groups } = selected
        return (
            positions.length > 0 ||
            workplaces.length > 0 ||
            typeOfRepresentative.length > 0 ||
            groups.length > 0
        )
    }

    return (
        <div className={classes.container}>
            <div className={classes.numberOfMembers}>
                <Typography size="XS">
                    Viser {members.length} medlemmer
                </Typography>
            </div>
            <div className={classes.pills}>
                {selected.groups.map((g) => {
                    return (
                        <Pill
                            label={g.name}
                            onClick={() => onUpdateGroups(g)}
                        />
                    )
                })}
                {selected.typeOfRepresentative.map((p) => {
                    return (
                        <Pill
                            label={p.name}
                            onClick={() => onUpdateTypeOfRepresentatives(p)}
                        />
                    )
                })}
                {selected.positions.map((p) => {
                    return (
                        <Pill
                            label={p.name}
                            onClick={() => onUpdatePositions(p)}
                        />
                    )
                })}
                {selected.workplaces.map((w) => {
                    return (
                        <Pill
                            label={w.name}
                            onClick={() => onUpdateWorkplaces(w)}
                        />
                    )
                })}
                {selected.representing.map((tr) => {
                    return (
                        <Pill
                            label={tr.displayName}
                            onClick={() => onUpdateTrFor(tr)}
                        />
                    )
                })}
                {selected.updateStatus.map((status) => {
                    return (
                        <Pill
                            label={status.name}
                            onClick={() => onUpdateUpdateStatus(status)}
                        />
                    )
                })}
                {ifAny() ? (
                    <button
                        type="button"
                        className={classes.button}
                        onClick={() => setSelected(initialFilter)}
                    >
                        <Typography
                            size="XS"
                            style={{
                                color: colors.green500,
                                textDecoration: 'underline',
                                paddingLeft: '10px',
                            }}
                        >
                            Ryd filtre
                        </Typography>
                    </button>
                ) : null}
            </div>
        </div>
    )
}
